import {keepPreviousData, queryOptions, useQuery} from "@tanstack/react-query"
import {DateRange} from "react-day-picker"

import * as apiClient from "@/lib/api"
import {ROLES} from "@/lib/auth/roles"
import {RecommendationsPerClientData} from "@/lib/elevation/types"
import {formatDate} from "@/lib/filters/date/date-utils"
import useIsQueryEnabled from "@/lib/hooks/query-enabler"
import {useQueryResetWithRefresh} from "@/lib/hooks/query-refetch-with-reset"
import {useDate} from "@/stores/date-filter"
import {useServiceType} from '@/stores/service-type'
import {useToken} from "@/stores/token"
import {QUERY_KEY_RECOMMENDATIONS_PER_CLIENT_DATA} from "@/utils/keys"

function fetchRecommendationsPerClient(date: DateRange, serviceType: number | null, professionalId: number | null): Promise<RecommendationsPerClientData> {
  const to = formatDate(date.to, 'yyyy-MM-dd')
  const from = formatDate(date.from, 'yyyy-MM-dd')
  return apiClient.get(`/firm/elevation/scores/recommendations-per-client?&to=${to}&from=${from}&service_type_id=${serviceType}${professionalId ? `&professional_id=${professionalId}` : ''}`)
}
export const recommendationsPerClientQueryOptions = (date: DateRange, serviceType: number | null,  professionalId: number | null) => queryOptions({
  queryKey: [QUERY_KEY_RECOMMENDATIONS_PER_CLIENT_DATA, date, serviceType, professionalId],
  queryFn: () => fetchRecommendationsPerClient(date, serviceType, professionalId),
  placeholderData: keepPreviousData
})

export const useRecommendationsPerClient = ( professionalId: number | null = null) => {
  const { date } = useDate()
  const { token } = useToken()
  const { serviceType } = useServiceType()

  const queryOptions = recommendationsPerClientQueryOptions(date, serviceType, professionalId)
  queryOptions.enabled = useIsQueryEnabled(!!(token?.roles.includes(ROLES.ROLE_ELEVATION) && serviceType))

  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}